import {useContext} from "@mytour/platform";
import {invalidatePageQueries} from "@mytour/platform/invalidatePageQueries";
import type {CustomerDetail, ICustomer} from "@mytour/shared";
import type {QueryClient, UseMutationOptions, UseQueryOptions} from "@tanstack/react-query";
import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import type {GetCustomersParams} from "./customerApi.service.js";
import {customerApiService} from "./customerApi.service.js";

type CustomerQueryData = ICustomer &
  Partial<
    Pick<CustomerDetail, "serviceAddresses" | "serviceAddressesVersion" | "tourRefreshFailed">
  >;

export const customerKeysStore = {
  root: ["customer"] as const,
  allCustomers: () => [...customerKeysStore.root, "list"] as const,
  customer: (id: string) => [...customerKeysStore.root, "detail", id] as const,
  positions: () => [...customerKeysStore.root, "positions"] as const,
  infiniteCustomers: (filter: string, size: number, sort?: string) =>
    [...customerKeysStore.root, "infinite", filter, size, sort] as const,
  lastEvent: (id: string) => [...customerKeysStore.root, "lastEvent", id] as const,
};

/**
 * Dernier passage du client, ou `null` — raccourci fiche client (#1254).
 * `GET /events/last-for-customer/:id` est `@Roles("manager")` : même gate que
 * `useCustomers` ci-dessous, pour la même raison (fiche client atteignable
 * depuis l'intervention d'un worker).
 */
export function useLastEventForCustomer(customerId: string) {
  const {context} = useContext();
  const {data, isLoading, refetch} = useQuery({
    queryKey: [...customerKeysStore.lastEvent(customerId), context?.companyId, context?.userId],
    queryFn: () => customerApiService.getLastEventForCustomer(customerId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    enabled: context?.role === "manager",
  });

  return {lastEvent: data, isLastEventLoading: isLoading, refetchLastEvent: refetch};
}

export function useCustomers(params: GetCustomersParams = {}) {
  const {context} = useContext();
  const {data, isLoading, isFetching, refetch} = useQuery({
    queryKey: [...customerKeysStore.allCustomers(), params],
    queryFn: () => customerApiService.getCustomers(params),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: context?.role === "manager",
  });

  return {
    customers: data?.data,
    customersCount: data?.totalCount,
    isCustomersLoading: isLoading,
    isCustomersFetching: isFetching,
    refetchCustomers: refetch,
  };
}

export function useInfiniteCustomers(filter: string, size: number, sort?: string) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: customerKeysStore.infiniteCustomers(filter, size, sort),
    queryFn: ({pageParam = 0}) =>
      customerApiService.getCustomerPage({
        filter,
        page: pageParam,
        size,
        sort,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
      return totalLoaded < lastPage.meta.totalCount ? allPages.length : undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const allCustomers = data?.pages.flatMap((page) => page.data) ?? [];
  const totalCount = data?.pages[0]?.meta.totalCount ?? 0;

  return {
    customers: allCustomers,
    customersCount: totalCount,
    customersPageMeta: data?.pages[0]?.meta,
    fetchNextCustomers: fetchNextPage,
    hasNextCustomers: hasNextPage,
    isCustomersLoading: isLoading,
    isCustomersError: isError,
    isCustomersFetchNextError: isFetchNextPageError,
    isCustomersFetching: isFetching,
    isCustomersFetchingNext: isFetchingNextPage,
    refetchCustomers: refetch,
  };
}

export function customerDetailQueryOptions(
  id: string | undefined,
  enabled = true,
  {staleTime = 5 * 60 * 1000}: {staleTime?: number} = {}
): UseQueryOptions<CustomerQueryData> {
  return {
    queryKey: customerKeysStore.customer(id as string),
    queryFn: () => customerApiService.getCustomer(id as string),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    staleTime,
  };
}

export function useCustomer(
  id: string | undefined,
  enabled = true,
  {staleTime = 5 * 60 * 1000}: {staleTime?: number} = {}
) {
  const {data, isLoading, isFetching, refetch, error} = useQuery(
    customerDetailQueryOptions(id, enabled, {staleTime})
  );

  return {
    customer: data,
    isCustomerLoading: isLoading,
    isCustomerFetching: isFetching,
    customerError: error,
    refetchCustomer: refetch,
  };
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (customer: ICustomer) => customerApiService.createCustomer(customer),
    async onSuccess(createdCustomer) {
      if (createdCustomer.id) {
        await queryClient.invalidateQueries({
          queryKey: customerKeysStore.customer(createdCustomer.id),
        });
      }
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.allCustomers(),
      });
      await invalidatePageQueries(queryClient, [...customerKeysStore.root, "infinite"]);
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.positions(),
      });
    },
    onError(error) {
      console.error("Failed to create customer:", error);
    },
  });

  return {
    createCustomer: mutateAsync,
    isCreatingCustomer: isPending,
  };
}

export function customerUpdateMutationOptions(
  queryClient: QueryClient
): UseMutationOptions<CustomerQueryData, Error, ICustomer> {
  return {
    mutationFn: (customer: ICustomer) => customerApiService.updateCustomer(customer),
    async onSuccess(updatedCustomer) {
      if (updatedCustomer.id) {
        queryClient.setQueryData(customerKeysStore.customer(updatedCustomer.id), updatedCustomer);
      }
      for (const queryKey of [
        ["agendaEvent"],
        ["event"],
        ["eventDayContext"],
        ["tour"],
        ["invoicing", "infinite"],
        ["serviceDescription", "current", "page"],
      ]) {
        await invalidatePageQueries(queryClient, queryKey);
      }
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.allCustomers(),
      });
      await invalidatePageQueries(queryClient, [...customerKeysStore.root, "infinite"]);
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.positions(),
      });
      if (updatedCustomer.id) {
        queryClient.invalidateQueries({queryKey: customerKeysStore.lastEvent(updatedCustomer.id)});
      }
    },
    onError(error) {
      console.error("Failed to update customer:", error);
    },
  };
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation(customerUpdateMutationOptions(queryClient));

  return {
    updateCustomer: mutateAsync,
    isUpdatingCustomer: isPending,
  };
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: (id: string) => customerApiService.deleteCustomer(id),
    async onSuccess(_data, deletedId) {
      queryClient.removeQueries({
        queryKey: customerKeysStore.customer(deletedId),
      });
      for (const queryKey of [
        ["agendaEvent"],
        ["event", "infinite"],
        ["invoicing", "infinite"],
        ["serviceDescription", "current", "page"],
      ]) {
        await invalidatePageQueries(queryClient, queryKey);
      }
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.allCustomers(),
      });
      await invalidatePageQueries(queryClient, [...customerKeysStore.root, "infinite"]);
      queryClient.invalidateQueries({
        queryKey: customerKeysStore.positions(),
      });
    },
    onError(error) {
      console.error("Failed to delete customer:", error);
    },
  });

  return {
    deleteCustomer: mutateAsync,
    isDeletingCustomer: isPending,
  };
}
